/* istanbul ignore file */
import { useState } from 'react';
import moment from 'moment';
import { get } from 'lodash';
import { MARK_PERFORMANCE_BASED_BONUS_AS_PAID, formatBonusData, useExpenseService } from 'domain/shared/expense';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import { FormFieldsData, DateInput, TextInput, CurrencyInput } from 'framework/form';
import { useDateService, DATE_FORMAT, getVisibility } from 'framework/utils';
import { useShowContext } from 'framework/show';
import { useExpenseId } from 'domain/finance/expenses/shared/useExpenseId';
import { CURRENCIES } from 'framework/ui/field';

type MarkAsPaidExpenseButtonProps = {
  isVisible: boolean | ((data: any) => boolean);
};

const MarkAsPaidBonusButton = ({ isVisible }: MarkAsPaidExpenseButtonProps) => {
  const id = useExpenseId();
  const { markBonusAsPaid, invalidateCache } = useExpenseService();
  const [payoutDateErrors, setPayoutDateErrors] = useState<string[]>([]);
  const [actualBonusSizeErrors, setActualBonusSizeErrors] = useState<string[]>([]);
  const { getToday, formatDateForAPI } = useDateService();
  const [payoutDate, setPayoutDate] = useState(getToday());
  const [bankTransactionId, setBankTransactionId] = useState<string>('');
  const [actualBonusSize, setActualBonusSize] = useState<string | number>('');
  const { data, setData = () => {} } = useShowContext();
  const compensationAmount = get(data, 'compensation.amount', 0);
  const compensationCurrency = get(data, 'compensation.currency', 0);

  if (!getVisibility(data, isVisible as any)) {
    return null;
  }

  const onPayoutDateChange = (values: FormFieldsData) => {
    setPayoutDate(values.payoutDate);
    const isValid = moment(values.payoutDate, DATE_FORMAT.API_ONLY_DATE, true).isValid();

    if (!values.payoutDate) {
      setPayoutDateErrors(['Field is required']);
    } else if (!isValid) {
      setPayoutDateErrors(['Please, choose the correct date']);
    } else {
      setPayoutDateErrors([]);
    }
  };

  const onActualBonusSizeChange = (values: FormFieldsData) => {
    setActualBonusSize(values.actualBonusSize);

    if (!values.actualBonusSize) {
      setActualBonusSizeErrors(['Field is required']);
    } else if (+values.actualBonusSize > compensationAmount) {
      setActualBonusSizeErrors(['Please, enter value less that compensation amount']);
    } else {
      setActualBonusSizeErrors([]);
    }
  };

  const onBankTransactionIdCallback = (values: FormFieldsData) => {
    setBankTransactionId(values.bankTransactionId);
  };

  return (
    <ButtonWithConfirmation
      displayMessage={`Do you really want to mark this expense (id: ${id}) as paid? Please fill out the inputs below.`}
      modalChildren={
        <>
          <DateInput
            source="payoutDate"
            value={payoutDate as any}
            label="Payout date"
            onChangeCallback={onPayoutDateChange}
            errorMessages={payoutDateErrors}
            isRequired
            sx={{ mt: 2.5 }}
          />
          <CurrencyInput
            source="actualBonusSize"
            label="Actual bonus size"
            value={actualBonusSize}
            onChangeCallback={onActualBonusSizeChange}
            maxDecimal={2}
            suffix={compensationCurrency ? CURRENCIES[compensationCurrency] : ''}
            errorMessages={actualBonusSizeErrors}
            isRequired
          />
          <TextInput
            source="bankTransactionId"
            label="Bank transaction ID"
            value={bankTransactionId}
            onChangeCallback={onBankTransactionIdCallback}
          />
        </>
      }
      submitMethod={() => markBonusAsPaid(id, formatDateForAPI(payoutDate), actualBonusSize, bankTransactionId)}
      afterSubmit={{
        successMessage: `The Expense has been successfully marked as paid.`,
        errorMessage: `Can't mark expense as paid.`,
        execute: (newExpenseData: any) => {
          invalidateCache();

          if (newExpenseData) {
            setData(formatBonusData(newExpenseData));
          }
        },
      }}
      permissionToCheck={MARK_PERFORMANCE_BASED_BONUS_AS_PAID}
      variant="outlined"
      confirmBtnProps={{
        disabled: !!payoutDateErrors.length || !payoutDate || !!actualBonusSizeErrors.length || !actualBonusSize,
      }}
    >
      Mark as paid
    </ButtonWithConfirmation>
  );
};

export default MarkAsPaidBonusButton;
